import {
  Ban, DollarSign, Eye, FileText, HandCoins, Handshake, Orbit, Pause, PowerOff,
  Rocket, ScanEye, Scale, ScrollText, Shield, Vote, type LucideIcon,
} from "lucide-react"
import generatedPolicies from "./policies.generated.json"

export type PolicyStatus = "current" | "in_discussion" | "off_the_table"

export type PolicyTimelineEntry = {
  // ISO date (YYYY-MM-DD) at which the policy enters this status. The
  // latest entry whose date is <= the active date wins; before the first
  // entry a policy is implicitly "off_the_table".
  date: string
  status: PolicyStatus
}

export type PolicyReference = {
  url: string
  source: string
  quote: string
}

export type Policy = {
  id: string
  label: string
  // Optional presentation extras; absent on policies sourced purely from
  // the spreadsheet snapshot.
  icon?: string
  description?: string
  shortReason?: string
  responsibility?: string
  reference?: PolicyReference
  timeline: PolicyTimelineEntry[]
}

// Lucide icon names usable in the `icon` field on a Policy. Kept available
// for future spreadsheet-sourced icons; unknown names render as a blank box.
export const POLICY_ICONS: Record<string, LucideIcon> = {
  Ban,
  DollarSign,
  Eye,
  FileText,
  HandCoins,
  Handshake,
  Orbit,
  Pause,
  PowerOff,
  Rocket,
  ScanEye,
  Scale,
  ScrollText,
  Shield,
  Vote,
}

// Only some policies carry an icon — the rest render as plain boxes, so the
// columns read as a sparse mix of glyphs and blanks rather than a wall of icons.
const POLICY_ICON_BY_ID: Record<string, string> = {
  "us-china-chip-audit": "Handshake",
  "pause-on-ai-r-d": "Pause",
  "compute-self-destruct-switches": "PowerOff",
  "citizen-s-dividend": "DollarSign",
  "universal-basic-galaxies": "Orbit",
}

// Policies kept out of the panel entirely (not even an "Off the Table" box)
// before this ISO date. Universal Basic Galaxies is a far-future idea that
// only makes sense once Plan A begins — the 2029 "Choose a Path" chapter —
// so it shouldn't sit in the 2026 widget as if it were already on the menu.
export const POLICY_VISIBLE_FROM_BY_ID: Record<string, string> = {
  "universal-basic-galaxies": "2029-01-01",
}

// Presentation-only box ordering. The HUD's policies panel stacks the three
// status rows vertically (Current / In Discussion / Off the Table) with each
// row's boxes right-aligned and kept in ALL_POLICIES order — so this sequence
// decides how far a box slides HORIZONTALLY when a status change moves it to
// another row. It is the exhaustive-search optimum for the current timeline
// data: a box changing rows keeps (nearly) its distance from the right edge
// and slides straight up instead of diagonally, and total horizontal travel
// across all transitions is minimized. Re-derive with
// `npx tsx scripts/planA/optimizePolicyDisplayOrder.ts` after timeline dates
// change. Ids missing from this list (e.g. new spreadsheet rows) sort after
// the listed ones, keeping their generated order.
const POLICY_DISPLAY_ORDER: string[] = [
  "us-china-chip-audit",
  "total-research-transparency",
  "pause-on-ai-r-d",
  "safety-regulation-for-large-training-runs",
  "compute-self-destruct-switches",
  "massive-ai-alignment-subsidies",
  "baseline-ai-company-transparency",
  "cap-and-trade-for-robots-and-compute",
  "citizen-s-dividend",
  "universal-basic-galaxies",
]

const displayOrderRank = (id: string): number => {
  const rank = POLICY_DISPLAY_ORDER.indexOf(id)
  return rank === -1 ? POLICY_DISPLAY_ORDER.length : rank
}

// Source of truth: data/sheet-snapshots/plan-a-side-panel.csv. Re-run
// `npx tsx scripts/planA/generatePoliciesData.ts` to refresh
// policies.generated.json after editing the snapshot.
// Keep presentation-only icons out of the generated spreadsheet payload.
export const ALL_POLICIES: Policy[] = (generatedPolicies.policies as Policy[])
  .map(policy => ({
    ...policy,
    icon: policy.icon ?? POLICY_ICON_BY_ID[policy.id],
  }))
  .sort((a, b) => displayOrderRank(a.id) - displayOrderRank(b.id))

export const POLICY_STATUS_LABELS: Record<PolicyStatus, string> = {
  current: "Current Policy",
  in_discussion: "In Discussion",
  off_the_table: "Off the Table",
}

export const POLICY_STATUS_ORDER: PolicyStatus[] = [
  "current",
  "in_discussion",
  "off_the_table",
]

const toIsoDateKey = (date: Date): string => date.toISOString().slice(0, 10)

export const getPolicyStatusForDate = (policy: Policy, date: Date): PolicyStatus => {
  const dateKey = toIsoDateKey(date)
  let activeStatus: PolicyStatus = "off_the_table"
  for (const entry of policy.timeline) {
    if (entry.date <= dateKey) activeStatus = entry.status
    else break
  }
  return activeStatus
}

export type PoliciesByStatus = Record<PolicyStatus, Policy[]>

export const getPoliciesByStatusForDate = (date: Date): PoliciesByStatus => {
  const dateKey = toIsoDateKey(date)
  const grouped: PoliciesByStatus = {
    current: [],
    in_discussion: [],
    off_the_table: [],
  }
  for (const policy of ALL_POLICIES) {
    const visibleFrom = POLICY_VISIBLE_FROM_BY_ID[policy.id]
    if (visibleFrom !== undefined && dateKey < visibleFrom) continue
    grouped[getPolicyStatusForDate(policy, date)].push(policy)
  }
  return grouped
}
